import React from 'react';

export type FmBannerTone = 'error' | 'warning' | 'info' | 'success';
/** Section state the banner reports; it decides the tone and the live-region role. */
export type FmBannerStatus = 'loading' | 'empty' | 'partial' | 'stale' | 'locked' | 'error';

const STATUS_TONE: Record<FmBannerStatus, FmBannerTone> = {
  loading: 'info',
  empty: 'info',
  partial: 'warning',
  stale: 'warning',
  locked: 'info',
  error: 'error',
};

export interface FmBannerProps {
  /** Status colour + default icon. Ignored when `status` is set. */
  tone?: FmBannerTone;
  /** Report a section state: maps to a tone, and only `error` interrupts as an alert. */
  status?: FmBannerStatus;
  /** When the reported data was observed, e.g. for a stale state. */
  timestamp?: React.ReactNode;
  /**
   * `lead` is for a state that replaces a whole section: the title is the
   * section's level-2 heading, so the outline still names it, at the notice
   * scale every state notice shares.
   */
  /**
   * `inline` is an unframed note beside content that is still shown (a partial
   * or stale section), so it does not push that content down like a full card.
   */
  size?: 'default' | 'lead' | 'inline';
  /** Headline line (AntD Alert `message` equivalent). */
  title?: React.ReactNode;
  /** Secondary explanatory copy (AntD Alert `description` equivalent). */
  description?: React.ReactNode;
  /** Trailing action slot (e.g. a retry FmButton). */
  action?: React.ReactNode;
  /** Override the default tone icon; pass `null` to hide it. */
  icon?: React.ReactNode | null;
  /** Show the close affordance; fires onClose. */
  onClose?: () => void;
  /** Body content rendered under title/description. */
  children?: React.ReactNode;
  /**
   * @internal DS-internal className prop; forbidden in route-owned code.
   */
  className?: string;
  'data-testid'?: string;
}

const cx = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join(' ');

const TONE_ICON: Record<FmBannerTone, string> = {
  error: '✕',
  warning: '!',
  info: 'i',
  success: '✓',
};

/**
 * Status notice banner — the Lehti AI DS replacement for AntD `Alert`. Owns its
 * own status colour, icon, title/description, optional action and dismiss; no
 * AntD, no page-local inline styles. Tones map 1:1 to Alert `type`
 * (error/warning/info/success).
 */
function bannerRole(status: FmBannerStatus | undefined, tone: FmBannerTone): 'alert' | 'status' {
  if (status) return status === 'error' ? 'alert' : 'status';
  return tone === 'error' || tone === 'warning' ? 'alert' : 'status';
}

export const FmBanner: React.FC<FmBannerProps> = ({
  tone: toneProp = 'info',
  status,
  timestamp,
  size = 'default',
  title,
  description,
  action,
  icon,
  onClose,
  children,
  className,
  'data-testid': dataTestId,
}) => {
  const tone = status ? STATUS_TONE[status] : toneProp;
  const TitleTag = size === 'lead' ? 'h2' : 'div';
  const resolvedIcon = icon === undefined
    ? <span className="fm-banner__glyph" aria-hidden="true">{TONE_ICON[tone]}</span>
    : icon;
  return (
    <div
      className={cx('fm-banner', `fm-banner--${tone}`, size !== 'default' && `fm-banner--${size}`, className)}
      role={bannerRole(status, tone)}
      data-status={status}
      data-testid={dataTestId}
    >
      {resolvedIcon ? <div className="fm-banner__icon">{resolvedIcon}</div> : null}
      <div className="fm-banner__body">
        <div className="fm-banner__content">
          {title ? <TitleTag className="fm-banner__title">{title}</TitleTag> : null}
          {description ? <div className="fm-banner__description">{description}</div> : null}
          {timestamp ? <span className="fm-banner__timestamp">{timestamp}</span> : null}
          {children}
        </div>
        {action ? <div className="fm-banner__action">{action}</div> : null}
      </div>
      {onClose ? (
        <button
          type="button"
          className="fm-banner__close"
          onClick={onClose}
          aria-label="Close"
        >
          ✕
        </button>
      ) : null}
    </div>
  );
};

export default FmBanner;
